// The frame: a top bar with the product name and connection state, the page, and three tabs.
import type { ReactNode } from "react";
import { useAppState } from "../app/AppContext";
import { onLinkClick, useRoute } from "../app/router";
import { PRODUCT_NAME } from "../brand";
import { machineStatus, needsCount } from "../lib/model";
import { IconBack, IconInbox, IconLink, IconRecent } from "./Icons";

export function Brand() {
  return (
    <h1>
      <span className="brandmark" aria-hidden="true">
        <i />
        <i />
      </span>
      {PRODUCT_NAME}
    </h1>
  );
}

export function TopBar({ title, back, right }: { title?: string; back?: string; right?: ReactNode }) {
  return (
    <header className="topbar">
      {back ? (
        <a className="back" href={back} onClick={(e) => onLinkClick(e, back)} aria-label={`Back to ${title ?? "inbox"}`}>
          <IconBack />
          <span>{title ?? "Inbox"}</span>
        </a>
      ) : (
        <Brand />
      )}
      {right}
    </header>
  );
}

export function ConnectionChip() {
  const s = useAppState();
  const ms = machineStatus(s);
  const name = s.machine?.name ?? s.pairing?.machine.name ?? "PC";
  if (s.removed) return <span className="chip chip-danger">Removed</span>;
  if (!s.online) return <span className="chip chip-unknown">Offline</span>;
  if (s.conn === "connecting" || s.conn === "reconnecting") return <span className="chip chip-unknown">Connecting</span>;
  if (ms === "online")
    return (
      <span className="chip chip-ok">
        <span className="mark mark-online" aria-hidden="true" />
        {name}
      </span>
    );
  if (ms === "unreachable")
    return (
      <span className="chip chip-danger">
        <span className="mark mark-off" aria-hidden="true" />
        {name} unreachable
      </span>
    );
  return <span className="chip chip-unknown">{name}</span>;
}

export function TabBar() {
  const route = useRoute();
  const s = useAppState();
  const needs = needsCount(s.cards, s.sends);
  const tab = (href: string, name: "inbox" | "recent" | "connections", label: string, icon: ReactNode, count?: number) => {
    const active = route.name === name || (name === "inbox" && route.name === "card");
    return (
      <a href={href} onClick={(e) => onLinkClick(e, href)} aria-current={active ? "page" : undefined} aria-label={count ? `${label}, ${count} need an answer` : label}>
        {icon}
        <span>{label}</span>
        {count ? <span className="count" aria-hidden="true">{count}</span> : null}
      </a>
    );
  };
  return (
    <nav className="tabbar" aria-label="Main">
      {tab("/", "inbox", "Inbox", <IconInbox />, needs)}
      {tab("/recent", "recent", "Recent", <IconRecent />)}
      {tab("/connections", "connections", "Connections", <IconLink />)}
    </nav>
  );
}

export function Page({ children, tabs = true, className = "" }: { children: ReactNode; tabs?: boolean; className?: string }) {
  return <main className={`page${tabs ? "" : " no-tabs"} ${className}`.trim()}>{children}</main>;
}
